import { observer } from 'mobx-react-lite'
import React, { useContext, useState, useCallback } from 'react'
import { components as reactSelectComponents } from 'react-select'

// evertel
import {
    Button, Row, Col, Icon, Text, useUI,
    Modal, ModalBody, ModalHeader, ModalFooter,
    PeopleSearchSelect, CustomOptionProps,
    FloatingMenu, MenuItem
} from '@evertel/web/ui'
import { CurrentUserController, useUserTagline } from '@evertel/blue-user'
import { UserTileRawData } from '@evertel/web/user'
import { useInvite } from '@evertel/web/invites'
import { useService } from '@evertel/di'
import { RoomController } from '@evertel/room'
import { APIDataBlueUser } from '@evertel/types'
import { RoomContext } from '../../RoomContext'


interface AddRoomMembersModalProps {
    visible: boolean
    onClose: () => void
}

const AddRoomMembersModal: React.FC<AddRoomMembersModalProps> = observer(({
    visible,
    onClose
}) => {

    const { roomController, canManageRoom } = useContext(RoomContext)
    const currentUserController = useService(CurrentUserController, [])
    const { openInvite } = useInvite()
    const { isConfirmed, addToast } = useUI()
    const room = roomController.room

    // State
    const [loadingUserId, setLoadingUserId] = useState<number | null>(null)

    // Computed values
    const isAgencyWideRoom = room?.options?.autoAddDepartmentMembers
    const isCurrentUserGuest = currentUserController.isGuestInDepartment(room?.departmentId)

    const handleInviteGuests = () => {
        openInvite('room-management-invite-guest', [{
            label: room?.name,
            value: room?.id
        }])
    }

    // Unified action handler matching mobile
    const handleUserAction = useCallback(async (action: string, selectedUser: APIDataBlueUser) => {
        if (!selectedUser) return

        try {
            setLoadingUserId(selectedUser.id)

            switch (action) {
                case 'make_member':
                    await roomController.addMember(selectedUser.id)
                    break
                case 'make_manager':
                    await roomController.addManager(selectedUser.id)
                    break
                case 'remove': {
                    const isSelectedManager = roomController.managerIds?.includes(selectedUser.id)
                    const confirmed = await isConfirmed({
                        title: isSelectedManager ? 'Remove Manager' : 'Remove Member',
                        message: `Are you sure you want to remove ${selectedUser.firstName} ${selectedUser.lastName} from this room?`,
                        acceptButton: {
                            label: 'Remove',
                            color: 'danger'
                        }
                    })
                    if (confirmed) {
                        await roomController.removeUser(selectedUser.id)
                    }
                    break
                }
                default:
                    break
            }
        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error?.message || 'An error occurred'
            })
        } finally {
            setLoadingUserId(null)
        }
    }, [roomController, isConfirmed])

    if (!room) return null

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            style={{ minHeight: 'calc(100vh - 4rem)' }}
            size="lg">
            <ModalHeader>
                <Row
                    align="left"
                    className="w-100">
                    <Text className="text-break" tag="h3" heavy>
                        Add People to
                        <Icon
                            name="hashtag"
                            className="ps-2 pe-1"
                            size={25}
                        />
                        {room.name}
                    </Text>
                </Row>
            </ModalHeader>
            <ModalBody id={'add-room-member-body'}>
                <Row>
                    <Col valign="top">
                        {(visible) &&
                            <PeopleSearchSelect
                                searchOnly={true}
                                searchScope="department"
                                whereFilter={{ emailVerified: true }}
                                departmentId={room.departmentId}
                                isMulti={false}
                                className="select-inline search no-bg mt-2"
                                menuIsOpen
                                maxMenuHeight="calc(80vh - 220px)"
                                customComponents={{ Option: UserItemRow }}
                                autoFocus
                                customProps={{
                                    departmentId: room.departmentId,
                                    roomController,
                                    canManageRoom,
                                    isCurrentUserGuest,
                                    isAgencyWideRoom,
                                    loadingUserId,
                                    onUserAction: handleUserAction
                                }}
                                onSelect={() => { /* */ }}
                            />
                        }
                    </Col>
                </Row>
            </ModalBody>
            {!isCurrentUserGuest && (
                <ModalFooter>
                    <Row>
                        <Col
                            xs={1}
                            align="right"
                            valign="top"
                            className="p-0">
                            <Icon
                                name="person-walking-dashed-line-arrow-right"
                                color="primary"
                                className="pt-1"
                                size={30}
                            />
                        </Col>
                        <Col className="ms-1" xs={8}>
                            <Text
                                tag="h4"
                                bold>
                                Need to bring people in from outside your agency?
                            </Text>
                            <Text tag="h5">
                                Invite people from other agencies, outside vendors, federal partners, etc.
                            </Text>
                        </Col>
                        <Col
                            xs={3}
                            align="right"
                            valign="center">
                            <Button
                                color="primary"
                                onClick={handleInviteGuests}>
                                Invite Guests
                            </Button>
                        </Col>
                    </Row>
                </ModalFooter>
            )}
        </Modal>
    )
})


interface UserItemRowProps {
    departmentId: number
    roomController: RoomController
    canManageRoom: boolean
    isCurrentUserGuest: boolean
    isAgencyWideRoom: boolean
    loadingUserId: number | null
    onUserAction: (action: string, user: APIDataBlueUser) => Promise<void>
}

const UserItemRow = observer((props: CustomOptionProps<APIDataBlueUser, UserItemRowProps>) => {
    const { data: user, selectProps } = props
    const { departmentId, roomController, canManageRoom, isCurrentUserGuest, isAgencyWideRoom, loadingUserId, onUserAction } = selectProps.customProps

    const { displayPosition, isGuest: isUserGuest } = useUserTagline({
        departmentsAccess: user.departmentsAccess || [],
        contextDepartmentId: departmentId,
        reportingDepartment: user.reportingDepartment
    }, { shortDeptName: false })

    const isMember = roomController?.memberIds?.includes(user.id)
    const isManager = roomController?.managerIds?.includes(user.id)
    const isUserLoading = loadingUserId === user.id

    // Permission check matching mobile - if false, don't show action menu at all
    const canManageRoomMembers = canManageRoom && !isCurrentUserGuest
    const canRemove = (isMember || isManager) && (!isAgencyWideRoom || (isAgencyWideRoom && isUserGuest))

    return (
        <reactSelectComponents.Option {...props}>
            <UserTileRawData
                user={user}
                imageWidth={40}
                className="py-1"
                isGuest={isUserGuest}
                subComponent={
                    <Text size="small">
                        {displayPosition}
                    </Text>
                }
                rightColComponent={canManageRoomMembers ? (
                    <FloatingMenu
                        attachId={'add-room-member-body'}
                        label="User Actions"
                        disabled={isUserLoading}
                        className={`btn btn-${(isManager || isMember) ? 'primary' : 'success'} text-nowrap`}
                        renderLabel={() => (
                            <>
                                {isManager ? 'Manager' : isMember ? 'Member' : 'Add'}
                                <Icon
                                    name="caret-down"
                                    type="solid"
                                    className="ms-1"
                                />
                            </>
                        )}
                    >
                        {isManager ? (
                            <MenuItem
                                label="Make a Member"
                                onClick={() => onUserAction('make_member', user)}
                            />
                        ) : isMember ? (
                            <MenuItem
                                label="Make a Manager"
                                onClick={() => onUserAction('make_manager', user)}
                            />
                        ) : (
                            <>
                                <MenuItem
                                    label="Add as Member"
                                    onClick={() => onUserAction('make_member', user)}
                                />
                                <MenuItem
                                    label="Add as Manager"
                                    onClick={() => onUserAction('make_manager', user)}
                                />
                            </>
                        )}
                        {canRemove && (
                            <MenuItem
                                label="Remove from Room"
                                onClick={() => onUserAction('remove', user)}
                                className="text-danger-emphasis"
                            />
                        )}
                    </FloatingMenu>
                ) : null}
            />
        </reactSelectComponents.Option>
    )
})

export { AddRoomMembersModal }
